/**
 * Shared configure SKU family mapping (hub API + UI).
 *
 * ABB / finished-product (FP) aliases match SDTestApp `device_control.parse_sku` prefix routing:
 * ABB-FI-* → FI-*, FP-OC → FI-5A-W, FP-OH → FI-5A, FP-UG/PF-UG → FI-3C.
 */

import {
  getWirelessCellularCanonicalPrefixesSnapshot,
  sqlSkuMatchesWirelessCellularFamily as sqlSkuMatchesWirelessCellularFamilyFromRules,
} from "./wireless-cellular-sku-rules.js";

export type ConfigurationDeviceFamily = "patrolmanPlus" | "patrolman" | "reconRelay" | "recon" | "";

export function normalizeSku(sku: string | null | undefined): string {
  return String(sku ?? "").trim();
}

/** Finished SKUs that use ABB PDD catch labels (ABB- prefix or Fisher Pierce FP-/PF- catalogs). */
export function isAbbPddLabelSku(sku: string | null | undefined): boolean {
  const upper = normalizeSku(sku).toUpperCase();
  if (upper === "") return false;
  if (upper.startsWith("ABB-") || upper.startsWith("ABB ")) return true;
  if (upper.startsWith("FP-UG") || upper.startsWith("PF-UG")) return true;
  if (upper.startsWith("FP-OC") || upper.startsWith("FP-OH")) return true;
  return false;
}

/**
 * Map customer / finished-product prefixes onto SGS family prefixes for configure family detection.
 * Does not change stored finished SKUs — only classification inputs.
 */
export function normalizeConfigurationSkuForFamilyDetection(sku: string | null | undefined): string {
  let upper = normalizeSku(sku).toUpperCase();
  if (upper === "") return "";

  if (upper.startsWith("ABB-")) {
    upper = upper.slice("ABB-".length);
  }

  if (upper.startsWith("FP-OC")) {
    return `FI-5A-W${upper.slice("FP-OC".length)}`;
  }
  if (upper.startsWith("FP-OH")) {
    return `FI-5A${upper.slice("FP-OH".length)}`;
  }
  if (upper.startsWith("FP-UG")) {
    return `FI-3C${upper.slice("FP-UG".length)}`;
  }
  if (upper.startsWith("PF-UG")) {
    return `FI-3C${upper.slice("PF-UG".length)}`;
  }

  return upper;
}

/** Patrolman Plus (FI-5A-W*), Patrolman (FI-5A*), Recon Relay (DM-1*), Recon (FI-3C*). */
export function getDeviceFamily(sku: string | null | undefined): ConfigurationDeviceFamily {
  const upper = normalizeConfigurationSkuForFamilyDetection(sku);
  if (upper === "") return "";
  if (upper.startsWith("FI-5A-W")) return "patrolmanPlus";
  if (upper.startsWith("FI-5A")) return "patrolman";
  if (upper.startsWith("DM-1")) return "reconRelay";
  if (upper.startsWith("FI-3C")) return "recon";
  return "";
}

export function isConfigurationDeviceFamily(
  sku: string | null | undefined,
  family: Exclude<ConfigurationDeviceFamily, "">,
): boolean {
  return getDeviceFamily(sku) === family;
}

export function isReconSku(sku: string | null | undefined): boolean {
  return isConfigurationDeviceFamily(sku, "recon");
}

export function isPatrolmanSku(sku: string | null | undefined): boolean {
  return isConfigurationDeviceFamily(sku, "patrolman");
}

export function isPatrolmanPlusSku(sku: string | null | undefined): boolean {
  return isConfigurationDeviceFamily(sku, "patrolmanPlus");
}

export function isReconRelaySku(sku: string | null | undefined): boolean {
  return isConfigurationDeviceFamily(sku, "reconRelay");
}

/**
 * SQL equivalent of {@link normalizeConfigurationSkuForFamilyDetection}.
 * `skuExpression` must be a trusted internal SQL expression, never user input.
 */
export function sqlCanonicalConfigurationSkuFamilyExpression(skuExpression: string): string {
  const upper = `upper(trim(coalesce(${skuExpression}, '')))`;
  const withoutAbb = `regexp_replace(${upper}, '^ABB-', '')`;
  return `case
    when ${withoutAbb} like 'FP-OC%' then regexp_replace(${withoutAbb}, '^FP-OC', 'FI-5A-W')
    when ${withoutAbb} like 'FP-OH%' then regexp_replace(${withoutAbb}, '^FP-OH', 'FI-5A')
    when ${withoutAbb} like 'FP-UG%' then regexp_replace(${withoutAbb}, '^FP-UG', 'FI-3C')
    when ${withoutAbb} like 'PF-UG%' then regexp_replace(${withoutAbb}, '^PF-UG', 'FI-3C')
    else ${withoutAbb}
  end`;
}

/**
 * SQL family predicate for a trusted internal SKU column/expression.
 * Mirrors the TypeScript family predicates above.
 */
export function sqlSkuMatchesConfigurationDeviceFamily(
  skuExpression: string,
  family: Exclude<ConfigurationDeviceFamily, "">,
): string {
  const canonical = sqlCanonicalConfigurationSkuFamilyExpression(skuExpression);
  const prefix = {
    patrolmanPlus: "FI-5A-W",
    patrolman: "FI-5A",
    reconRelay: "DM-1",
    recon: "FI-3C",
  }[family];
  if (family === "patrolman") {
    return `(${canonical} like '${prefix}%' and ${canonical} not like 'FI-5A-W%')`;
  }
  return `(${canonical} like '${prefix}%')`;
}

/**
 * SQL expression for the SKU string passed to `parse_sku_current`.
 *
 * Customer finished-product aliases (`FP-UG`, `PF-UG`, `FP-OC`, `FP-OH`) may carry an optional
 * `ABB-` prefix; strip it before parse because templates are keyed on the customer SKU rows.
 * `ABB-FI-*` assembly outputs keep the stored SKU so template 2/17 `ABB` parse slots apply.
 */
export function sqlConfigurationSkuParseInputExpression(skuExpression: string): string {
  const trimmed = `trim(${skuExpression})`;
  const upper = `upper(${trimmed})`;
  const withoutAbb = `regexp_replace(${upper}, '^ABB-', '')`;
  return `case
    when ${withoutAbb} like 'FP-UG%' or ${withoutAbb} like 'PF-UG%' or ${withoutAbb} like 'FP-OC%' or ${withoutAbb} like 'FP-OH%'
      then regexp_replace(${trimmed}, '^ABB-', '', 'i')
    else ${trimmed}
  end`;
}

/**
 * Parse core feature from the stored finished SKU.
 *
 * `parse_sku_current` resolves BOM templates against customer SKU rows (FP-UG*, PF-UG*, FP-OC*, FP-OH*).
 * Canonical FI-3C* names often lack `public.sku` rows, so family routing uses
 * {@link sqlCanonicalConfigurationSkuFamilyExpression} separately instead of rewriting here.
 */
export function sqlConfigurationSkuCoreFeatureExpression(skuExpression: string): string {
  return `parse_sku_current(${sqlConfigurationSkuParseInputExpression(skuExpression)}) ->> 'coreFeature'`;
}

/**
 * Wireless SKUs require IMEI + ICCID and portal writes.
 * Active prefixes come from manufacturing.wireless_cellular_sku_rule (cached at boot).
 */
export function skuRequiresImeiIccid(sku: string | null | undefined): boolean {
  const upper = normalizeConfigurationSkuForFamilyDetection(sku);
  if (upper === "") {
    return false;
  }
  return getWirelessCellularCanonicalPrefixesSnapshot().some((prefix) => upper.startsWith(prefix));
}

/**
 * SQL predicate for cellular wireless finished SKUs (DB-backed rule table).
 */
export function sqlSkuMatchesWirelessCellularFamily(skuExpression: string): string {
  return sqlSkuMatchesWirelessCellularFamilyFromRules(skuExpression);
}

/** PCBA test is skipped for wireless configure-only families. */
export function skipsPcbaTestRecordRequirement(sku: string | null | undefined): boolean {
  return skuRequiresImeiIccid(sku);
}

export function isSkuSupportedForConfigure(sku: string | null | undefined): boolean {
  return getDeviceFamily(sku) !== "";
}

export function unsupportedSkuMessage(sku: string | null | undefined): string {
  return (
    `SKU is not supported for Configure: ${normalizeSku(sku) || "(blank)"}. ` +
    "Supported families: Patrolman Plus (FI-5A-W* / FP-OC*), Patrolman (FI-5A* / FP-OH*), " +
    "Recon (FI-3C* / FP-UG* / PF-UG*), Recon Relay (DM-1*), including ABB-FI-* aliases."
  );
}
